/**
 * Splits a team's full name into the two lines the watch cards render.
 *
 * The player API sends `away_team` as the WHOLE name ("Maryland Black Bears")
 * and `away_team_mascot` as its tail ("Black Bears"). Passing the whole name as
 * the first line printed the mascot twice:
 *
 *   MARYLAND BLACK BEARS
 *   BLACK BEARS
 *
 * Removing the mascot from the end leaves the part that belongs on the first
 * line, which is the club's place or programme ("Maryland", "SSM").
 *
 * Only a real suffix is removed. A mascot that is not the tail of the full name
 * is left alone rather than cut from the middle, because a tenant whose two
 * fields disagree should see both of them rather than a mangled one.
 *
 * A full name equal to its own mascot collapses to a single line: returning an
 * empty first line is what the cards read as "this team has one line", and the
 * alternative is printing the same word twice, which is the bug this fixes.
 */
export function teamNameParts(
  fullName?: string | null,
  mascot?: string | null,
): { city: string; mascot: string } {
  const full = (fullName ?? "").trim();
  const nick = (mascot ?? "").trim();

  if (!full) return { city: "", mascot: nick };
  if (!nick) return { city: full, mascot: "" };

  const haystack = full.toLowerCase();
  const needle = nick.toLowerCase();

  if (haystack === needle) return { city: "", mascot: nick };

  if (haystack.endsWith(needle)) {
    const city = full.slice(0, full.length - nick.length).trim();
    return city ? { city, mascot: nick } : { city: "", mascot: nick };
  }

  return { city: full, mascot: nick };
}
